import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FileText, Loader2, Search, ArrowLeft, Send, ChevronDown } from "lucide-react";
import { AppModal, AppButton } from "../ui";
import { varGroupsForAudience, dynamicVarLabel } from "./whatsappTemplateVars";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-26 (Phase 5); template-send-modal.scss deleted.
//
// Most of that sheet's 35 literals were DEAD FALLBACKS — `var(--text-secondary,
// #667781)` on a token that is declared, so the hex could never render. They were
// WhatsApp's own greys, written as the fallback for our tokens, which is the one
// place a reader would go looking for the app's colour and find someone else's.
//
// What did render, and is now a token:
//   · the three template-status pills — WhatsApp green / amber / slate at 15%
//     with hand-picked inks — are the contract's `-soft`/`-fg` shape. `APPROVED`
//     is a STATUS here, not WhatsApp chrome: it is Meta's review verdict on our
//     template, so it is `--success`, not `--ext-whatsapp`.
//   · the preview bubble stays WhatsApp's (TOKEN_CONTRACT §8) and keeps the
//     FIXED `--ext-whatsapp-*` pair, deliberately: it previews what the recipient
//     will see in their own client, so it must not follow our theme. Its
//     hardcoded `#111b21` ink was `--ext-whatsapp-ink` written out by hand.
//
// ⚠️ `tsm-mode-btn--active` is a STANDALONE string, not the base plus overrides.
// `tw-border-line` and `tw-border-primary` both set border-color and resolve by
// Tailwind's own source order, not the order they are written in the attribute.
//
// The `<select>` caret was two `linear-gradient()` triangles. It is a positioned
// lucide `<ChevronDown>` now — the same shape `PreMeetingCheck` and
// `collect-fee-v2` settled on — which is why the select gained a relative wrapper.

const RECIPIENT = "tw-mb-3 tw-text-[0.85rem] tw-text-content-muted";
const LOADER =
  "tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-6 tw-text-[0.9rem] " +
  "tw-text-content-muted";

// ── Search ──
const SEARCH = "tw-relative tw-mb-3";
const SEARCH_ICON =
  "tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-content-subtle";
const SEARCH_INPUT =
  "tw-w-full tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-alt " +
  "tw-py-2 tw-pl-8 tw-pr-3 tw-text-[0.9rem] tw-text-content tw-outline-none " +
  "focus:tw-border-primary";

// ── Template list ──
const TPL_LIST =
  "tw-m-0 tw-flex tw-max-h-[50vh] tw-list-none tw-flex-col tw-gap-[0.4rem] " +
  "tw-overflow-y-auto tw-p-0";
const TPL =
  "tw-w-full tw-cursor-pointer tw-rounded-sm tw-border tw-border-solid tw-border-line " +
  "tw-bg-surface-alt tw-px-3 tw-py-[0.6rem] tw-text-left " +
  "tw-transition-[border-color,background-color] tw-duration-150 " +
  "hover:tw-border-primary hover:tw-bg-surface-hover";
const TPL_TOP = "tw-flex tw-items-center tw-gap-2";
// tw-font-mono is --font-mono, the app's one mono stack; the sheet named its own
// (`ui-monospace, SFMono-Regular, …`), which is a second vocabulary for one label.
const TPL_NAME = "tw-font-mono tw-text-[0.88rem] tw-font-semibold tw-text-content";
const TPL_LANG =
  "tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface tw-px-[7px] " +
  "tw-py-px tw-text-[0.7rem] tw-text-content-muted";
const TPL_STATUS =
  "tw-ml-auto tw-rounded-md tw-px-2 tw-py-[2px] tw-text-[0.65rem] tw-font-semibold tw-uppercase";
const TPL_STATUS_TONE = {
  approved: "tw-bg-success/15 tw-text-success-fg",
  pending: "tw-bg-warning/15 tw-text-warning-fg",
  other: "tw-bg-muted tw-text-muted-fg",
};
const TPL_BODY =
  "tw-mt-[0.35rem] tw-line-clamp-2 tw-whitespace-pre-wrap tw-text-[0.78rem] " +
  "tw-leading-[1.4] tw-text-content-muted";

const EMPTY = "tw-px-4 tw-py-6 tw-text-center tw-text-content-subtle";
const EMPTY_TEXT = "tw-mb-1 tw-mt-2 tw-text-[0.9rem] tw-text-content-muted";
const EMPTY_HINT = "tw-text-[0.78rem]";

// ── Fill-variables step ──
const FILL_HEAD = "tw-mb-[0.85rem] tw-flex tw-items-center tw-gap-2";
const NO_VARS = "tw-mb-[0.85rem] tw-mt-0 tw-text-[0.85rem] tw-text-content-muted";
const FIELDS = "tw-mb-4 tw-flex tw-flex-col tw-gap-[0.7rem]";
const FIELD = "tw-flex tw-flex-col tw-gap-1";
// The media-header URL sits outside the field list (it is not a text variable),
// so it carries its own bottom spacing.
const FIELD_MEDIA = FIELD + " tw-mb-4";
const FIELD_LABEL =
  "tw-text-[0.78rem] tw-font-semibold tw-capitalize tw-text-content-muted";
const FIELD_INPUT =
  "tw-w-full tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface " +
  "tw-px-[0.7rem] tw-py-2 tw-text-[0.9rem] tw-text-content tw-outline-none " +
  "focus:tw-border-primary";
const FIELD_SELECT = FIELD_INPUT + " tw-cursor-pointer tw-appearance-none tw-pr-[1.8rem]";
const FIELD_SELECT_CARET =
  "tw-pointer-events-none tw-absolute tw-right-[0.7rem] tw-top-1/2 tw--translate-y-1/2 " +
  "tw-text-content-subtle";
const FIELD_HINT = "tw-text-[0.72rem] tw-leading-[1.4] tw-text-content-subtle";
// Standalone note under the field list, not a per-field hint.
const FIELD_HINT_BLOCK = FIELD_HINT + " tw--mt-[0.4rem] tw-mb-4";
const FIELD_MODES = "tw-flex tw-gap-1";

// Static/Dynamic switch. Deliberately small — it sits above every variable, and
// the variable's own control is what the eye should land on.
const MODE_BTN =
  "tw-cursor-pointer tw-rounded-[6px] tw-border tw-border-solid tw-border-line " +
  "tw-bg-transparent tw-px-[9px] tw-py-[2px] tw-text-[0.7rem] tw-font-semibold " +
  "tw-leading-[1.5] tw-text-content-subtle hover:tw-text-content-muted";
const MODE_BTN_ACTIVE =
  "tw-cursor-pointer tw-rounded-[6px] tw-border tw-border-solid tw-border-primary-solid " +
  "tw-bg-primary-solid tw-px-[9px] tw-py-[2px] tw-text-[0.7rem] tw-font-semibold " +
  "tw-leading-[1.5] tw-text-primary-solid-fg";

const PREVIEW_LABEL =
  "tw-text-[0.72rem] tw-font-semibold tw-uppercase tw-tracking-[0.03em] tw-text-content-subtle";
const PREVIEW_BUBBLE =
  "tw-mt-[0.35rem] tw-whitespace-pre-wrap tw-break-words tw-rounded-sm " +
  "tw-bg-[var(--ext-whatsapp-bubble-out)] tw-px-[0.8rem] tw-py-[0.6rem] tw-text-[0.86rem] " +
  "tw-leading-[1.45] tw-text-[var(--ext-whatsapp-ink)]";

const FOOTER = "tw-flex tw-w-full tw-justify-between tw-gap-2";

/**
 * TemplateSendModal — pick an approved WhatsApp template, fill its dynamic
 * variables, and send it.
 *
 * This is the ONLY way to message a contact outside the 24-hour customer-service
 * window (or for the first time), so it's wired into both the reply bar (re-open
 * an expired chat) and the New Message composer (first contact to a new number).
 *
 * Each variable is either **Static** (a literal typed here) or **Dynamic** (an
 * LMS field resolved server-side against this recipient). Dynamic is what stops
 * an operator hand-typing the student's own name, course, batch or pending fees
 * into a message the server could fill itself — and hand-typing is how those
 * values end up wrong.
 *
 * Props:
 *   show         — boolean
 *   onHide       — close handler
 *   fetchTemplates() → Promise<template[]>   (each: { name, language, category, status, components })
 *   onSend({ templateName, languageCode, components, paramConfig }) → Promise
 *       Caller is responsible for the actual API call + refresh. Should throw on failure.
 *       `paramConfig` is present only when at least one slot is Dynamic; the
 *       backend then resolves it and ignores the text params in `components`.
 *   showToast(msg, type)
 *   title        — optional header title (default "Send template message")
 *   recipientLabel — optional string shown as the send target (name/phone)
 *   audienceKind — "student" when the recipient is a known student (offers the
 *       full student/course/batch/fees catalog), otherwise "contact" (institute
 *       and date fields only). Must match what the backend can resolve: it
 *       decides from the recipient, and strips anything the recipient can't
 *       answer for, so offering more here would just render blanks.
 *   allowDynamic — set false when the send endpoint behind `onSend` does not
 *       understand `paramConfig`. The modal then behaves exactly as it did
 *       before dynamic fields existed: every slot is a literal. Offering the
 *       picker against an endpoint that ignores it would send empty parameters,
 *       and the operator would only find out from the delivered message.
 */

const VAR_RE = /\{\{\s*([\w]+)\s*\}\}/g;

/** Pull {{1}} / {{name}} tokens out of a component's text, in order, de-duped. */
function extractTokens(text) {
  const seen = new Set();
  const tokens = [];
  let m;
  VAR_RE.lastIndex = 0;
  while ((m = VAR_RE.exec(text || "")) !== null) {
    if (!seen.has(m[1])) { seen.add(m[1]); tokens.push(m[1]); }
  }
  return tokens;
}

/**
 * Build the editable field list for a template (HEADER text + BODY variables).
 * Each field: { comp: 'header'|'body', token, named, paramName, example, label }.
 */
function buildFields(template) {
  if (!template) return [];
  const fields = [];
  const comps = template.components || [];

  const headerComp = comps.find(
    (c) => (c.type || "").toUpperCase() === "HEADER" && (c.format || "TEXT").toUpperCase() === "TEXT",
  );
  const bodyComp = comps.find((c) => (c.type || "").toUpperCase() === "BODY");

  const pushFor = (comp, kind, examplePositional, exampleNamed) => {
    if (!comp?.text) return;
    const tokens = extractTokens(comp.text);
    if (!tokens.length) return;
    const named = tokens.some((t) => Number.isNaN(Number(t)));
    tokens.forEach((token, i) => {
      let example = "";
      if (named) {
        const hit = (exampleNamed || []).find((p) => p?.param_name === token);
        example = hit?.example || "";
      } else {
        example = (examplePositional || [])[i] || "";
      }
      fields.push({
        comp: kind,
        token,
        named,
        paramName: named ? token : undefined,
        example,
        label: named ? token.replace(/_/g, " ") : `Variable {{${token}}}`,
      });
    });
  };

  pushFor(
    headerComp, "header",
    headerComp?.example?.header_text,
    headerComp?.example?.header_text_named_params,
  );
  pushFor(
    bodyComp, "body",
    bodyComp?.example?.body_text?.[0],
    bodyComp?.example?.body_text_named_params,
  );

  return fields;
}

/**
 * Render the BODY text with the user's values substituted, for live preview.
 * A dynamic slot previews as its bracketed label — the real value is only known
 * server-side, and showing a bracket is honest about that.
 */
function renderPreview(template, values, modes) {
  const bodyComp = (template?.components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
  let text = bodyComp?.text || "";
  text = text.replace(VAR_RE, (_, token) => {
    const key = `body:${token}`;
    const v = values[key];
    if (!v) return `{{${token}}}`;
    return modes[key] === "dynamic" ? `[${dynamicVarLabel(v)}]` : v;
  });
  return text;
}

const statusBadgeClass = (s) => {
  const tone = s === "APPROVED" ? "approved" : s === "PENDING" ? "pending" : "other";
  return `${TPL_STATUS} ${TPL_STATUS_TONE[tone]}`;
};

const TemplateSendModal = ({
  show,
  onHide,
  fetchTemplates,
  onSend,
  showToast,
  title = "Send template message",
  recipientLabel,
  audienceKind = "contact",
  allowDynamic = true,
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [values, setValues] = useState({}); // { "body:1": "...", "header:name": "..." }
  /* Same keys as `values`: "static" (literal) | "dynamic" (LMS field). */
  const [modes, setModes] = useState({});
  const [sending, setSending] = useState(false);
  /* Public URL of the media to put in an IMAGE/VIDEO/DOCUMENT header. The
     template's approved sample is only a review artefact — every send has to
     supply its own media, and Meta rejects the message without it. */
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");

  const reset = useCallback(() => {
    setSelected(null);
    setValues({});
    setModes({});
    setSearch("");
    setHeaderMediaUrl("");
  }, []);

  const varGroups = useMemo(() => varGroupsForAudience(audienceKind), [audienceKind]);

  // Load approved templates when opened.
  useEffect(() => {
    if (!show) { reset(); return; }
    let active = true;
    setLoading(true);
    Promise.resolve(fetchTemplates())
      .then((list) => {
        if (!active) return;
        const approved = (list || []).filter((t) => (t.status || "").toUpperCase() === "APPROVED");
        setTemplates(approved);
      })
      .catch(() => { if (active) setTemplates([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [show, fetchTemplates, reset]);

  const fields = useMemo(() => buildFields(selected), [selected]);

  /* IMAGE / VIDEO / DOCUMENT header format on the selected template, or "". */
  const mediaHeaderFormat = useMemo(() => {
    const hdr = (selected?.components || []).find((c) => (c.type || "").toUpperCase() === "HEADER");
    const fmt = (hdr?.format || "").toUpperCase();
    return ["IMAGE", "VIDEO", "DOCUMENT"].includes(fmt) ? fmt : "";
  }, [selected]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) => t.name.toLowerCase().includes(q) || (t.category || "").toLowerCase().includes(q),
    );
  }, [templates, search]);

  const handleSelect = (tpl) => {
    setSelected(tpl);
    // Default to Static prefilled with Meta's example, so a template with no
    // personalisation stays a two-click send.
    const initial = {};
    const initialModes = {};
    buildFields(tpl).forEach((f) => {
      const k = `${f.comp}:${f.token}`;
      initial[k] = f.example || "";
      initialModes[k] = "static";
    });
    setValues(initial);
    setModes(initialModes);
  };

  /* Switching mode clears the value: a literal and a variable name are not
     interchangeable, and carrying one over would send the wrong thing. */
  const setFieldMode = (key, mode) => {
    setModes((m) => ({ ...m, [key]: mode }));
    setValues((v) => ({ ...v, [key]: "" }));
  };

  const allFilled = fields.every((f) => (values[`${f.comp}:${f.token}`] || "").trim())
    && (!mediaHeaderFormat || !!headerMediaUrl.trim());

  const anyDynamic = fields.some((f) => modes[`${f.comp}:${f.token}`] === "dynamic");

  const handleSend = async () => {
    if (!selected) return;
    // Assemble Meta components grouped by header/body, preserving variable order.
    const components = [];

    /* A media header is its own parameter shape — { type: 'image', image: { link } } —
       and never coexists with header text params, so it short-circuits the
       text grouping below. */
    if (mediaHeaderFormat) {
      const kind = mediaHeaderFormat.toLowerCase();   // image | video | document
      components.push({
        type: "header",
        parameters: [{ type: kind, [kind]: { link: headerMediaUrl.trim() } }],
      });
    }

    const group = (kind) => {
      if (kind === "header" && mediaHeaderFormat) return;
      const fs = fields.filter((f) => f.comp === kind);
      if (!fs.length) return;
      components.push({
        type: kind,
        parameters: fs.map((f) => ({
          type: "text",
          ...(f.named ? { parameter_name: f.paramName } : {}),
          // A dynamic slot has no client-side value; the backend overwrites this
          // whole component from paramConfig. Blank rather than the variable
          // name, so a backend that ignored paramConfig would send an obvious
          // gap instead of the literal text "pending_fees".
          text: modes[`${f.comp}:${f.token}`] === "dynamic"
            ? ""
            : (values[`${f.comp}:${f.token}`] || "").trim(),
        })),
      });
    };
    group("header");
    group("body");

    /* Only sent when something is actually dynamic — a fully static send keeps
       the original wire shape, so nothing changes for templates that don't
       personalise. The key is the Meta token ({{1}} → "1"), which is what the
       backend's positional-vs-named rule keys off. */
    const paramConfig = anyDynamic ? { header: [], body: [], button: [] } : null;
    if (paramConfig) {
      fields.forEach((f) => {
        const k = `${f.comp}:${f.token}`;
        const isDynamic = modes[k] === "dynamic";
        paramConfig[f.comp].push({
          key: f.token,
          type: isDynamic ? "dynamic" : "static",
          ...(isDynamic ? { variable: values[k] || "" } : { value: (values[k] || "").trim() }),
        });
      });
    }

    setSending(true);
    try {
      await onSend({
        templateName: selected.name,
        languageCode: selected.language || "en",
        components,
        ...(paramConfig ? { paramConfig } : {}),
      });
      onHide?.();
    } catch (err) {
      showToast?.(err?.response?.data?.message || err?.message || "Failed to send template", "error");
    }
    setSending(false);
  };

  const footer = selected ? (
    <div className={FOOTER}>
      <AppButton variant="ghost" size="sm" onClick={() => { setSelected(null); setValues({}); setModes({}); }} disabled={sending}>
        <ArrowLeft size={14} /> Back
      </AppButton>
      <AppButton size="sm" onClick={handleSend} disabled={sending || !allFilled}>
        {sending ? <Loader2 size={14} className="tw-animate-spin" /> : <Send size={14} />} Send template
      </AppButton>
    </div>
  ) : null;

  return (
    <AppModal
      show={show}
      onHide={onHide}
      title={title}
      icon={<FileText size={18} />}
      size="md"
      footer={footer}
    >
      {recipientLabel && (
        <div className={RECIPIENT}>To: <strong>{recipientLabel}</strong></div>
      )}

      {loading ? (
        <div className={LOADER}><Loader2 size={18} className="tw-animate-spin" /> Loading templates…</div>
      ) : !selected ? (
        /* ── Step 1: choose a template ─────────────────────────────── */
        <div>
          <div className={SEARCH}>
            <Search size={15} className={SEARCH_ICON} />
            <input
              className={SEARCH_INPUT}
              placeholder="Search templates"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          {filtered.length === 0 ? (
            <div className={EMPTY}>
              <FileText size={28} />
              <p className={EMPTY_TEXT}>No approved templates available.</p>
              <span className={EMPTY_HINT}>Create and get a template approved in the Templates tab first.</span>
            </div>
          ) : (
            <ul className={TPL_LIST}>
              {filtered.map((t) => {
                const bodyComp = (t.components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
                return (
                  <li key={`${t.name}:${t.language}`}>
                    <button type="button" className={TPL} onClick={() => handleSelect(t)}>
                      <div className={TPL_TOP}>
                        <span className={TPL_NAME}>{t.name}</span>
                        <span className={TPL_LANG}>{t.language}</span>
                        <span className={statusBadgeClass(t.status)}>{t.status}</span>
                      </div>
                      {bodyComp?.text && <div className={TPL_BODY}>{bodyComp.text}</div>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : (
        /* ── Step 2: fill variables ────────────────────────────────── */
        <div>
          <div className={FILL_HEAD}>
            <span className={TPL_NAME}>{selected.name}</span>
            <span className={TPL_LANG}>{selected.language}</span>
          </div>

          {mediaHeaderFormat && (
            <label className={FIELD_MEDIA}>
              <span className={FIELD_LABEL}>
                Header · {mediaHeaderFormat.toLowerCase()} URL
              </span>
              <input
                className={FIELD_INPUT}
                value={headerMediaUrl}
                onChange={(e) => setHeaderMediaUrl(e.target.value)}
                placeholder="https://example.com/banner.jpg"
              />
              <span className={FIELD_HINT}>
                Must be a public HTTPS link. The sample approved with the template
                is not reused — each message supplies its own {mediaHeaderFormat.toLowerCase()}.
              </span>
            </label>
          )}

          {fields.length === 0 && !mediaHeaderFormat ? (
            <p className={NO_VARS}>This template has no variables — ready to send.</p>
          ) : fields.length === 0 ? null : (
            <div className={FIELDS}>
              {fields.map((f) => {
                const key = `${f.comp}:${f.token}`;
                const mode = modes[key] || "static";
                return (
                  <div key={key} className={FIELD}>
                    <span className={FIELD_LABEL}>
                      {f.comp === "header" ? "Header · " : ""}{f.label}
                    </span>

                    {allowDynamic && (
                      <div className={FIELD_MODES}>
                        <button
                          type="button"
                          className={mode === "dynamic" ? MODE_BTN_ACTIVE : MODE_BTN}
                          onClick={() => setFieldMode(key, "dynamic")}
                        >
                          Dynamic
                        </button>
                        <button
                          type="button"
                          className={mode === "static" ? MODE_BTN_ACTIVE : MODE_BTN}
                          onClick={() => setFieldMode(key, "static")}
                        >
                          Static
                        </button>
                      </div>
                    )}

                    {mode === "dynamic" ? (
                      <div className="tw-relative">
                        <select
                          className={FIELD_SELECT}
                          value={values[key] || ""}
                          onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                        >
                          <option value="">Choose a field…</option>
                          {varGroups.map(({ group, vars }) => (
                            <optgroup key={group} label={group}>
                              {vars.map((v) => (
                                <option key={v.value} value={v.value}>{v.label}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        <ChevronDown size={14} className={FIELD_SELECT_CARET} />
                      </div>
                    ) : (
                      <input
                        className={FIELD_INPUT}
                        value={values[key] || ""}
                        onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                        placeholder={f.example ? `e.g. ${f.example}` : "Enter value"}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {allowDynamic && audienceKind !== "student" && fields.length > 0 && (
            <p className={FIELD_HINT_BLOCK}>
              This recipient isn't linked to a student, so only institute and date
              fields can be filled in automatically.
            </p>
          )}

          <div>
            <span className={PREVIEW_LABEL}>Preview</span>
            <div className={PREVIEW_BUBBLE}>{renderPreview(selected, values, modes)}</div>
          </div>
        </div>
      )}
    </AppModal>
  );
};

export default TemplateSendModal;
